import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined'
import Typography from '@mui/material/Typography'

import TagMenu from 'components/Shared/TagMenu'
import { useAppDispatch, useAppSelector } from 'redux/Hook'
import {
  bulkTags,
  fetchAllTags,
  fetchContributions,
} from 'redux/thunks/TextAnalysis'
import {
  resetAllTags,
  resetSelectedContributions,
  setLoading,
} from 'redux/slices/TextAnalysis'
import { Tag } from '../Types'
import { handleAddNewBulkTag } from '../Helpers/BulkSelectHelpers'
import { useAlert } from 'components/Shared/Alert'

const StyledButton = styled(Button)<{ $active: boolean }>`
  gap: 0.5rem;
  background-color: ${({ $active }) =>
    $active ? 'rgba(0, 125, 255, 0.08)' : 'inherit'};

  @media only screen and (max-width: 700px) {
    display: none !important;
  }
`

const BulkAddTag: React.FC = () => {
  const dispatch = useAppDispatch()
  const { showAlert } = useAlert()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const {
    allContributions: { doneFetching },
    selectedContributions: selectedRows,
    tags: { tagsList, fetchingTags },
    contributions: {
      bulkUpdate: { done: bulkUpdateDone, error },
    },
  } = useAppSelector((state) => state.textAnalysis)

  useEffect(() => {
    /**
     * If bulkUpdate API is executed successfully:
     *  1. Remove all selected checkboxes
     *  2. Fetch contributions
     * else:
     *  1. Remove all selected checkboxes
     *  2. Hide loading
     */
    if (bulkUpdateDone) {
      dispatch(resetSelectedContributions())
      dispatch(fetchContributions())
    } else if (error) {
      showAlert({ title: error })
      dispatch(resetSelectedContributions())
      dispatch(setLoading(false))
    }
  }, [bulkUpdateDone, error, dispatch])

  useEffect(() => {
    /**
     * Once contributions are fetched:
     * 1. Hide loading
     */
    if (doneFetching) {
      dispatch(setLoading(false))
    }
  }, [doneFetching, dispatch])

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      dispatch(fetchAllTags())
      setAnchorEl(event.currentTarget)
      setMenuOpen(true)
    },
    [dispatch],
  )

  const handleCloseTagMenu = useCallback(
    (selectedTags: (string | Tag)[], tagsChangedStatus: boolean) => {
      handleClose()
      if (tagsChangedStatus) {
        dispatch(setLoading(true))
        const payload = handleAddNewBulkTag(selectedTags, selectedRows)
        dispatch(bulkTags(payload))
      }
    },
    [dispatch, selectedRows],
  )

  const handleClose = useCallback(() => {
    setMenuOpen(false)
    setAnchorEl(null)
    buttonRef.current?.focus()
    dispatch(resetAllTags())
  }, [dispatch])

  const handleKeyDown = useCallback(
    (
      e: React.KeyboardEvent,
      selectedTags: string[],
      tagsChangedStatus: boolean,
    ) => {
      if (['Escape', 'Tab'].includes(e.key)) {
        e.preventDefault()
        handleCloseTagMenu(selectedTags, tagsChangedStatus)
      }
    },
    [handleCloseTagMenu],
  )

  return (
    <Box data-testid="ta-bulk-tagging">
      <StyledButton
        ref={buttonRef}
        onClick={handleClick}
        $active={menuOpen}
        disabled={!selectedRows.length}
      >
        <AddCircleOutlineOutlinedIcon fontSize="small" />
        <Typography variant="button">ADD TAG</Typography>
      </StyledButton>
      <TagMenu
        id="bulk-add-tag-menu"
        fetching={fetchingTags}
        tags={tagsList}
        placement="bottom-start"
        label="Add Tag"
        placeholder="Search"
        tagLabel="name"
        allowCreateTag={false}
        contributionTags={[]}
        open={menuOpen}
        anchorEl={anchorEl}
        onCloseTagMenu={handleCloseTagMenu}
        onAddTagToContribution={() => null}
        onTagDelete={() => null}
        cardActionsRef={() => null}
        handleKeyDown={handleKeyDown}
      />
    </Box>
  )
}

export default React.memo(BulkAddTag)
